import { useState } from 'react'
import {
  LineChart as RechartLineChart,
  Line, XAxis, YAxis, ResponsiveContainer, Tooltip,
} from 'recharts'
import type { WeightTrajectory } from '../lib/weightDebug'
import { LB_PER_GRAM } from '../lib/weightDebug'

const CELL_COLORS = ['#355E3B', '#D8613C', '#F0C070', '#9B3C3C', '#7a5a52', '#a07a72']

type Mode = 'raw' | 'weight'
type OutlierMode = 'no' | '99' | '95' | '90'

function quantile(sorted: number[], q: number): number {
  const pos = (sorted.length - 1) * q
  const base = Math.floor(pos)
  const rest = pos - base
  const next = sorted[base + 1]
  return next === undefined ? sorted[base] : sorted[base] + rest * (next - sorted[base])
}

// Central-percentile clip (mirrors kodiak-proto charts.js): null out values outside
// the kept band so the auto y-axis rescales to the bulk and a few wild spikes don't
// flatten the trace. Display-only — unrelated to the device `outlier` flag.
function clipOutliers(vals: (number | null)[], mode: OutlierMode): (number | null)[] {
  if (mode === 'no') return vals
  const present = vals.filter((v): v is number => v != null).sort((a, b) => a - b)
  if (present.length < 5) return vals
  const tail = (100 - Number(mode)) / 2 / 100
  const lo = quantile(present, tail)
  const hi = quantile(present, 1 - tail)
  return vals.map(v => (v == null || v < lo || v > hi ? null : v))
}

function Radio<T extends string>({
  name, value, current, onChange, label,
}: { name: string; value: T; current: T; onChange: (v: T) => void; label: string }) {
  return (
    <label className="inline-flex items-center gap-1 cursor-pointer">
      <input
        type="radio"
        name={name}
        checked={current === value}
        onChange={() => onChange(value)}
        className="accent-bear w-2.5 h-2.5"
      />
      {label}
    </label>
  )
}

export default function PerCellReadings({ trajectory }: { trajectory: WeightTrajectory }) {
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<Mode>('raw')
  const [outlierMode, setOutlierMode] = useState<OutlierMode>('no')
  const { readings, cellKeys, cellCalibration } = trajectory
  const enabledByCh = Object.fromEntries(cellCalibration.map(c => [c.ch, c.enabled]))
  const minT = readings[0].t

  return (
    <div className="rounded-lg bg-stone-50 border border-stone-100 px-3 py-2.5">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center justify-between w-full cursor-pointer"
      >
        <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-stone-500">
          Per-cell readings
        </span>
        <svg
          viewBox="0 0 16 16"
          className={`w-3.5 h-3.5 text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
        >
          <path d="M4 6l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div className="mt-2 space-y-2">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[9px] text-stone-500">
            <span className="inline-flex items-center gap-2">
              <span className="text-stone-400">value:</span>
              <Radio name="cell-mode" value="raw" current={mode} onChange={setMode} label="raw (ADC)" />
              <Radio name="cell-mode" value="weight" current={mode} onChange={setMode} label="weight (lb)" />
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="text-stone-400">exclude outliers:</span>
              {(['no', '99', '95', '90'] as OutlierMode[]).map(m => (
                <Radio key={m} name="cell-outlier" value={m} current={outlierMode} onChange={setOutlierMode} label={m} />
              ))}
            </span>
          </div>

          {cellKeys.map((ch, i) => {
            const rawVals = readings.map(r => {
              const c = r.cells[ch]
              if (!c) return null
              return mode === 'raw' ? c.raw : c.weight * LB_PER_GRAM
            })
            const vals = clipOutliers(rawVals, outlierMode)
            const data = readings.map((r, idx) => ({ t: r.t, v: vals[idx] }))
            const enabled = enabledByCh[ch] !== false
            return (
              <div key={ch} className={enabled ? '' : 'opacity-40'}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[9px] text-stone-500">cell {ch}</span>
                  {!enabled && (
                    <span className="font-mono text-[8px] uppercase tracking-wider text-coral">disabled</span>
                  )}
                </div>
                <ResponsiveContainer width="100%" height={48}>
                  <RechartLineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                    <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']} hide />
                    <YAxis
                      tick={{ fill: '#a8a29e', fontFamily: 'JetBrains Mono, monospace', fontSize: 8 }}
                      tickFormatter={(v: number) => (mode === 'raw' ? `${Math.round(v)}` : v.toFixed(2))}
                      tickLine={false}
                      axisLine={false}
                      width={44}
                      domain={['auto', 'auto']}
                    />
                    <Tooltip
                      contentStyle={{ background: '#fff', border: '1px solid #e7e5e4', fontFamily: 'JetBrains Mono, monospace', fontSize: 10, borderRadius: 6 }}
                      labelStyle={{ color: '#78716c' }}
                      formatter={(v: number) => (mode === 'raw' ? [`${Math.round(v)}`, 'raw'] : [v.toFixed(2), 'lb'])}
                      labelFormatter={(t: number) => `${(t - minT).toFixed(1)}s`}
                      isAnimationActive={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="v"
                      stroke={CELL_COLORS[i % CELL_COLORS.length]}
                      strokeWidth={1.25}
                      dot={false}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  </RechartLineChart>
                </ResponsiveContainer>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
